// src/views/adminView/contractsView/contractDocumentFile.js
// ─────────────────────────────────────────────────────────────────────────────
// Lectura del PDF firmado EN EL NAVEGADOR, antes de subirlo: firma binaria `%PDF-` y
// sha256. contracts vuelve a mirar los primeros bytes del objeto en S3 y usa el sha256 para
// el historial, la idempotencia y la conciliación tras un corte de red; acá se adelanta el
// rechazo para no subir a S3 un archivo que después se va a rechazar.
//
// Con FileReader y no con `file.arrayBuffer()`: el jsdom de react-scripts test no lo
// implementa (mismo motivo que cobro/utils/proofBlob.js).
// ─────────────────────────────────────────────────────────────────────────────

// `%PDF-`
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d];

function readAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo.'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * true si el archivo empieza con `%PDF-`. Lee solo los primeros bytes.
 * @param {Blob} file
 * @returns {Promise<boolean>}
 */
export async function hasPdfSignature(file) {
  const head = new Uint8Array(await readAsArrayBuffer(file.slice(0, PDF_SIGNATURE.length)));
  return PDF_SIGNATURE.every((byte, i) => head[i] === byte);
}

/**
 * sha256 del archivo completo, en hex minúscula (64 caracteres).
 * Lanza si el navegador no expone WebCrypto: `crypto.subtle` solo existe en contextos
 * seguros (https o localhost).
 * @param {Blob} file
 * @returns {Promise<string>}
 */
export async function sha256Hex(file) {
  const subtle = window.crypto?.subtle;
  if (!subtle) throw new Error('WebCrypto no disponible en este contexto.');
  const digest = await subtle.digest('SHA-256', await readAsArrayBuffer(file));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
